/**
 * Configuración centralizada de enlaces de Stripe por plan y moneda.
 *
 * Estructura:
 *  - Cada plan tiene sus variantes (anual, trimestral) y con experts hub (solo anuales)
 *  - Cada variante tiene un enlace EUR y uno USD
 *
 * Uso:
 *  import { STRIPE_LINKS, getStripeLink } from '../core';
 * 
 *  get planProUrl(): string {
 *    return getStripeLink('pro', this.selectedPlan as BillingPeriod, this.geoPricingService.getCurrency());
 *  }
 * 
 */

export type BillingPeriod = 'anual' | 'trimestral';

export type StripeCurrency = 'EUR' | 'USD';

export interface StripePlanLinks {
    EUR: string;
    USD: string;
}

export interface StripePlan {
    anual: StripePlanLinks;
    trimestral?: StripePlanLinks;
    anualConHub?: StripePlanLinks;  // add-on experts hub incluido en el precio
}

export const STRIPE_LINKS: Record<string, StripePlan> = {
    // Plan Start
    start: {
        anual: {
            EUR: 'https://buy.stripe.com/eVq4gz8WXfQr1n58zYaMU0w',
            USD: 'https://buy.stripe.com/dRmaEXdddgUvd5N6rQaMU0G',
        },
        trimestral: {
            EUR: 'https://buy.stripe.com/9B6cN56OP1ZB8Px03saMU0x',
            USD: 'https://buy.stripe.com/5kQcN56OPeMn2r98zYaMU0H',
        },
        anualConHub: {
            EUR: 'https://buy.stripe.com/aFafZhgpp9s3c1J2bAaMU0u',
            USD: 'https://buy.stripe.com/dRm3cvc997jVc1J4jIaMU0D',
        },
    },
    // Plan Intermedio
    intermedio: {
        anual: {
            EUR: 'https://buy.stripe.com/bJe7sLc990Vx4zh4jIaMU0y',
            USD: 'https://buy.stripe.com/cNiaEX3CDdIj6HpcQeaMU0I',
        },
        trimestral: {
            EUR: 'https://buy.stripe.com/5kQ8wPddddIj6Hp2bAaMU0z',
            USD: 'https://buy.stripe.com/aFaeVd0qr9s3d5NaI6aMU0J',
        },
        anualConHub: {
            EUR: 'https://buy.stripe.com/9B6aEX6OP33F8Px03saMU0v',
            USD: 'https://buy.stripe.com/28EdR9fllgUv8Px5nMaMU0E',
        },
    },
    // Plan Pro
    pro: {
        anual: {
            EUR: 'https://buy.stripe.com/00w8wPgpp5bN4zh4jIaMU0A',
            USD: 'https://buy.stripe.com/cNi00j7STcEfe9R4jIaMU0K',
        },
        trimestral: {
            EUR: 'https://buy.stripe.com/8x23cv2yzaw71n52bAaMU0B',
            USD: 'https://buy.stripe.com/eVq4gz6OP7jV7LtbMaaMU0L',
        },
    },
    // IKI Experts Hub
    expertsHub: {
        anual: {
            EUR: 'https://buy.stripe.com/bJe6oHfll0Vx4zh3fEaMU0s',
            USD: 'https://buy.stripe.com/14A5kDehh5bN0j1aI6aMU0F',
        },
    },
};

// Helper
// Si el enlace USD no está disponible aún, hace fallback al EUR.
export function getStripeLink(
    plan: string,
    period: BillingPeriod,
    currency: StripeCurrency,
    conHub: boolean = false,
): string {
    const planLinks = STRIPE_LINKS[plan];
    if (!planLinks) return '';

    const variant = conHub && planLinks.anualConHub && period === 'anual'
        ? planLinks.anualConHub
        : planLinks[period];

    if (!variant) return '';

    const url = variant[currency];
    // Fallback a EUR si el enlace USD aún no está configurado
    return url || variant['EUR'] || '';
}
